import { find, isNil } from 'lodash';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';

export type CardAccessLevel = {
  type: API.AccessLevelType;
  title: string;
  description: string;
};

export type ConfigAccessLevel = {
  type: API.ConfigAccessLevel;
  title: string;
  description: string;
};

export type AccessLevelMenuOption = Pick<CardAccessLevel, 'title' | 'description'>;

// The order of this array determines the order of the access levels in the UI.
export const cardAccessLevels: CardAccessLevel[] = [
  {
    type: 'FULL',
    title: i18n('channel.cardAccess.FULL.title'),
    description: i18n('channel.cardAccess.FULL.description'),
  },
  {
    type: 'CREATE',
    title: i18n('channel.cardAccess.CREATE.title'),
    description: i18n('channel.cardAccess.CREATE.description'),
  },
  {
    type: 'EDIT',
    title: i18n('channel.cardAccess.EDIT.title'),
    description: i18n('channel.cardAccess.EDIT.description'),
  },
  {
    type: 'VIEW',
    title: i18n('channel.cardAccess.VIEW.title'),
    description: i18n('channel.cardAccess.VIEW.description'),
  },
  {
    type: 'PERSONAL',
    title: i18n('channel.cardAccess.PERSONAL.title'),
    description: i18n('channel.cardAccess.PERSONAL.description'),
  },
];

export const configAccessLevels: ConfigAccessLevel[] = [
  {
    type: 'FULL',
    title: i18n('channel.configAccess.FULL.title'),
    description: i18n('channel.configAccess.FULL.description'),
  },
  {
    type: 'VIEW_EDITOR',
    title: i18n('channel.configAccess.VIEW_EDITOR.title'),
    description: i18n('channel.configAccess.VIEW_EDITOR.description'),
  },
  {
    type: 'NO_ACCESS',
    title: i18n('channel.configAccess.NO_ACCESS.title'),
    description: i18n('channel.configAccess.NO_ACCESS.description'),
  },
];

export const defaultCardAccessLevel: API.AccessLevelType = 'FULL';
export const defaultConfigAccessLevel: API.ConfigAccessLevel = 'VIEW_EDITOR';

export function getCardAccessLevel(membership: API.ChannelMembershipV2Fragment): CardAccessLevel {
  const accessLevelType = membership.accessLevel.type;
  const accessLevel = find(cardAccessLevels, level => level.type === accessLevelType);
  if (isNil(accessLevel)) {
    throw new Error(`Card access level not found for type: ${accessLevelType}`);
  }
  return accessLevel;
}

export function getConfigAccessLevel(
  membership: API.ChannelMembershipV2Fragment,
): ConfigAccessLevel {
  const defaultConfigAccessLevelType = getConfigAccessForCardAccess(membership.accessLevel.type);
  const configAccessLevelType = membership.configAccessLevelType ?? defaultConfigAccessLevelType;
  const accessLevel = find(configAccessLevels, level => level.type === configAccessLevelType);
  if (isNil(accessLevel)) {
    throw new Error(`Config access level not found for type: ${configAccessLevelType}`);
  }
  return accessLevel;
}

/**
 * Derives a config access level from a card access level. Used as the null
 * fallback for members who were added to a channel before these code changes
 * and therefore have no persisted `configAccessLevelType` (see
 * {@link getConfigAccessLevel}). Newly-added members should instead be given
 * `defaultConfigAccessLevel` explicitly.
 */
export function getConfigAccessForCardAccess(
  accessLevelType: API.AccessLevelType,
): API.ConfigAccessLevel {
  if (accessLevelType === 'VIEW') {
    return 'NO_ACCESS';
  } else {
    return 'FULL';
  }
}
